/**
 * Which operators each field allows.
 *
 * Fixed here rather than in the control that draws them, for the reason the
 * contacts vocabulary gives: a second answer to "what may this field be asked"
 * is one that drifts, and the looser copy decides what gets saved.
 *
 * `created` and `changed` take a number of days and only `at_most` / `at_least`
 * — "changed within 7 days" and "not changed for 7 days". A date field with
 * `is` would be asking whether something happened on an exact timestamp, which
 * is never the question.
 */
const OPERATORS = {
    /*
     * `contains` first, and that ordering is the default a new rule gets.
     *
     * Somebody filtering by name types a fragment — "north" — and expects the
     * Northwind one. With `is` they would see nothing and conclude the filter was
     * broken, which is the failure this vocabulary already warns about elsewhere:
     * a rule that always matches nobody looks exactly like one that does not
     * work. `is` stays available for the person who means it.
     */
    name: ['contains', 'not_contains', 'is', 'is_not'],
    asks_about: ['is', 'is_not'],
    rules: ['is', 'at_least', 'at_most'],
    created: ['at_most', 'at_least'],
    changed: ['at_most', 'at_least'],
};
export function operatorsFor(field) {
    return OPERATORS[field];
}
/**
 * A fresh rule about a field, which is also what changing a rule's field gives.
 *
 * The operator and the value are reset rather than carried, and this is the one
 * place that decides so. The contacts board learnt why: its filter kept the old
 * operator when the field changed, so switching to a days field left it
 * comparing with `is`, which the domain refuses — the board then showed nobody
 * while its own control read "within".
 */
export function ruleFor(field) {
    const operator = OPERATORS[field][0];
    if (operator === undefined)
        throw new Error(`no operators for ${field}`);
    return { field, operator, value: '' };
}
/**
 * Whether a segment matches every rule.
 *
 * **An unfinished rule matches nothing rather than everything.** A filter is
 * typed one control at a time, so half-built is its normal state for most of
 * the time it exists — and the safe direction is to under-show. Widening back
 * to the whole list mid-edit is how somebody acts on a row they did not mean to
 * be looking at. The contacts board answers the same way.
 */
export function segmentMatches(segment, filter, now) {
    return filter.children.every((rule) => holds(segment, rule, now));
}
function holds(segment, rule, now) {
    if (rule.value.trim() === '')
        return false;
    if (rule.field === 'name')
        return textHolds(segment.name, rule);
    if (rule.field === 'asks_about')
        return asksAbout(segment.definition, rule);
    if (rule.field === 'rules')
        return numberHolds(segment.definition.children.length, rule);
    /*
     * The operator applies to the *age*, not to the date: "changed at most 7 days
     * ago" is "7 or fewer days have passed". Turning the question into a number
     * of days here keeps `numberHolds` the only place a comparison is written.
     */
    const when = rule.field === 'created' ? segment.createdAt : segment.updatedAt;
    return numberHolds(daysBetween(when, now), rule);
}
function textHolds(value, rule) {
    const looking = rule.value.trim().toLowerCase();
    const against = value.toLowerCase();
    if (rule.operator === 'is')
        return against === looking;
    if (rule.operator === 'is_not')
        return against !== looking;
    if (rule.operator === 'contains')
        return against.includes(looking);
    return !against.includes(looking);
}
function numberHolds(value, rule) {
    const against = Number(rule.value);
    if (Number.isNaN(against))
        return false;
    if (rule.operator === 'is')
        return value === against;
    if (rule.operator === 'at_least')
        return value >= against;
    if (rule.operator === 'at_most')
        return value <= against;
    return false;
}
/**
 * Every field the definition mentions, including inside an "any of" group.
 *
 * This is what makes "which segments still ask about `company`" answerable —
 * the question somebody has before they delete a declared field.
 */
function asksAbout(definition, rule) {
    const fields = new Set();
    for (const child of definition.children) {
        if ('any' in child)
            for (const inner of child.any)
                fields.add(inner.field);
        else
            fields.add(child.field);
    }
    const named = fields.has(rule.value.trim());
    return rule.operator === 'is' ? named : !named;
}
/** Whole days, rounded down: "changed today" is 0 and matches "within 1 day". */
function daysBetween(then, now) {
    return Math.floor((now.getTime() - then.getTime()) / 86_400_000);
}
//# sourceMappingURL=segmentFilter.js.map